

import { motion } from "framer-motion";

interface ParticleProps {
  size: number;
  x: number;
  y: number;
  delay: number;
}

const Particle = ({ size, x, y, delay }: ParticleProps) => {
  const rotation = Math.random() * 360;
  return (
    <motion.div
      className="absolute"
      style={{ 
        left: `${x}%`, 
        top: `${y}%`,
        width: 0,
        height: 0,
        borderLeft: `${size/2}px solid transparent`,
        borderRight: `${size/2}px solid transparent`,
        borderBottom: `${size}px solid #BCD6FF`,
        opacity: 0.1,
        transform: `rotate(${rotation}deg)`
      }}
      initial={{ opacity: 0, scale: 0 }}
      animate={{ 
        opacity: [0.1, 0.3, 0.1], 
        scale: [1, 1.3, 1],
        x: [0, 8, 0],
        y: [0, -8, 0],
        rotate: [`${rotation}deg`, `${rotation + 10}deg`, `${rotation}deg`]
      }}
      transition={{
        duration: 5 + Math.random() * 3,
        repeat: Infinity,
        delay: delay,
        ease: "easeInOut"
      }}
    />
  );
};

interface TriangleProps {
  size: number;
  x: number;
  y: number;
  rotation: number;
  delay: number;
  color: string;
}

const Triangle = ({ size, x, y, rotation, delay }: TriangleProps) => {
  return (
    <motion.div
      className="absolute"
      style={{ 
        left: `${x}%`, 
        top: `${y}%`,
        width: 0,
        height: 0,
        borderLeft: `${size/2}px solid transparent`,
        borderRight: `${size/2}px solid transparent`,
        borderBottom: `${size}px solid #BCD6FF`,
        transform: `rotate(${rotation}deg)`
      }}
      initial={{ opacity: 0 }}
      animate={{ 
        opacity: [0.1, 0.2, 0.1],
        rotate: [`${rotation}deg`, `${rotation + 15}deg`, `${rotation}deg`],
        x: [0, 20, 0],
        y: [0, -20, 0]
      }}
      transition={{
        duration: 8 + Math.random() * 4,
        repeat: Infinity,
        delay: delay,
        ease: "easeInOut"
      }}
    />
  );
};

const Wave = () => {
  return (
    <div className="absolute inset-0 overflow-hidden">
      <motion.div
        className="absolute w-[200%] h-[150px] bottom-0 left-[-50%] bg-gradient-to-r from-[#90C0FF]/5 via-[#90C0FF]/10 to-[#90C0FF]/5 rounded-[100%]"
        animate={{ 
          y: [0, -20, 0],
          scaleX: [1, 1.1, 1],
          scaleY: [1, 1.2, 1],
        }}
        transition={{
          duration: 15,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />
      <motion.div
        className="absolute w-[200%] h-[120px] bottom-[50px] left-[-50%] bg-gradient-to-r from-[#90C0FF]/5 via-[#90C0FF]/5 to-[#90C0FF]/5 rounded-[100%]"
        animate={{ 
          y: [0, -30, 0],
          scaleX: [1, 1.05, 1],
          scaleY: [1, 1.1, 1],
        }}
        transition={{
          duration: 18,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 1
        }}
      />
    </div>
  );
};



export const Background = () => {
  // Generate random positions for particles
  const particles = Array.from({ length: 15 }, (_, i) => ({
    id: i,
    size: 5 + Math.random() * 10,
    x: Math.random() * 100,
    y: Math.random() * 100,
    delay: Math.random() * 2
  }));
  
  // Generate random positions for triangles
  const triangles = Array.from({ length: 8 }, (_, i) => ({
    id: i,
    size: 30 + Math.random() * 70,
    x: Math.random() * 100,
    y: Math.random() * 100,
    rotation: Math.random() * 360,
    delay: Math.random() * 3,
    color: '#BCD6FF'
  }));
  
  return (
    <div className="fixed inset-0 overflow-hidden -z-10">
      {/* Gradient background */}
      <div className="absolute inset-0 bg-gradient-to-b from-white to-[#f5f5ff]" />
      
      {/* Particles */}
      {particles.map((particle) => (
        <Particle
          key={particle.id}
          size={particle.size}
          x={particle.x}
          y={particle.y}
          delay={particle.delay}
        />
      ))}
      
      {/* Triangles */}
      {triangles.map((triangle) => (
        <Triangle
          key={triangle.id}
          size={triangle.size}
          x={triangle.x}
          y={triangle.y}
          rotation={triangle.rotation}
          delay={triangle.delay}
          color={`${triangle.color}${triangle.id % 3 === 0 ? '10' : '05'}`}
        />
      ))}
      
      {/* Wave effect */}
      <Wave />
      

    </div>
  );
};
